import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentUser } from '../../../hooks/useCurrentUser'
import useApi from '../../../hooks/useApi'
import { isEventAdmin } from '../../../lib/event'
import { followingUserIdSetFromPayload } from '../../../lib/following'
import Icon from '../../../components/icon'
import Button from '../../../components/button'
import VolunteerRow from './VolunteerRow'
import { requestSignupReport } from './requestSignupReport'
import styles from './volunteerList.module.scss'

export default function TeamSidebar({ event }) {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const { getUserFollowing, emailParticipantsList } = useApi()
  const [followingIds, setFollowingIds] = useState(new Set())
  const [sendingReport, setSendingReport] = useState(false)
  const hasFetchedFollowingRef = useRef(false)
  const resetTimerRef = useRef(null)

  useEffect(() => {
    if (currentUser?.id && !hasFetchedFollowingRef.current) {
      hasFetchedFollowingRef.current = true

      const fetchFollowing = async () => {
        try {
          const response = await getUserFollowing({ id: currentUser.id })
          if (response?.data) {
            setFollowingIds(followingUserIdSetFromPayload(response.data))
          }
        } catch (error) {
        } finally {
          resetTimerRef.current = setTimeout(() => {
            hasFetchedFollowingRef.current = false
            resetTimerRef.current = null
          }, 5000)
        }
      }

      fetchFollowing()
    }

    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current)
        resetTimerRef.current = null
      }
    }
  }, [currentUser?.id, getUserFollowing])

  const canManage = isEventAdmin(event, currentUser)

  const organizers = useMemo(() => {
    const orgs = []
    const seenIds = new Set()

    if (event.user) {
      orgs.push({
        user: event.user,
        role: 'Organizer',
        id: event.user.id,
      })
      seenIds.add(event.user.id)
    }

    if (event.admins && Array.isArray(event.admins)) {
      event.admins.forEach(admin => {
        if (!seenIds.has(admin.id)) {
          orgs.push({
            user: admin,
            role: 'Admin',
            id: admin.id,
          })
          seenIds.add(admin.id)
        }
      })
    }

    if (event.tasks && Array.isArray(event.tasks)) {
      event.tasks.forEach(task => {
        if (task.task_leader_users && Array.isArray(task.task_leader_users)) {
          task.task_leader_users.forEach(leader => {
            if (!seenIds.has(leader.id)) {
              orgs.push({
                user: leader,
                role: 'Task Leader',
                id: leader.id,
              })
              seenIds.add(leader.id)
            }
          })
        }
      })
    }

    return orgs
  }, [event.user, event.admins, event.tasks])

  const showParticipantReport = canManage && !!event?.participant_signup

  const handleRequestParticipantReport = useCallback(async () => {
    if (!showParticipantReport || !event?.id || sendingReport) return

    setSendingReport(true)
    try {
      await requestSignupReport({
        email: currentUser?.email,
        send: () => emailParticipantsList({ id: event.id }),
        t,
      })
    } catch (error) {
      console.error('Error requesting participant report:', error)
      window.alert(
        t(
          'VolunteerListScreen.downloadAlertRequestError',
          'Unable to send the Sign Up Report. Please try again.',
        ),
      )
    } finally {
      setSendingReport(false)
    }
  }, [
    showParticipantReport,
    event?.id,
    sendingReport,
    currentUser?.email,
    emailParticipantsList,
    t,
  ])

  if (organizers.length === 0) {
    return null
  }

  return (
    <div className={styles.sidebar}>
      <div className={styles.sidebarHeader}>
        <h5 className={styles.sidebarTitle}>
          {t('event.team_heading', 'Team')}
        </h5>
        <Icon name="users" className={styles.sectionIcon} />
      </div>

      <div className={styles.previewList}>
        {organizers.map((org, index) => {
          const enrichedVolunteer = {
            id: org.id,
            user: {
              ...org.user,
              current_user_follows:
                org.user?.current_user_follows !== undefined
                  ? org.user.current_user_follows
                  : followingIds.has(org.user?.id),
            },
            role: org.role,
            created_at: null,
          }
          return (
            <VolunteerRow
              key={org.id || `org-${index}`}
              volunteer={enrichedVolunteer}
              event={event}
              isOrganizer={org.role === 'Organizer'}
              isAdmin={org.role === 'Admin'}
              onUpdate={() => {}}
              showCheckInButton={false}
            />
          )
        })}
      </div>

      {showParticipantReport && (
        <Button
          className={styles.csvButton}
          onClick={handleRequestParticipantReport}
          variant="primary"
          disabled={sendingReport}
        >
          <Icon name="file-csv" className="me-2" />
          {t('VolunteerListScreen.downloadAlertTitle', 'Sign Up Report')}
        </Button>
      )}
    </div>
  )
}
